import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, Grid, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GeneralEditor, GeneralSelectMenu } from 'src/components/general';
import { WIKI_MEDIA_LIST } from 'src/constants/queryKeys';
import { networkCreateMediaWiki, networkUpdateMediaWiki } from 'src/network/content-management/wiki-media';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import * as Yup from 'yup';
import { FormProvider, RHFSwitch, RHFTextField } from '../../../../components/hook-form';

// ----------------------------------------------------------------------

export default function WikiMediaForm() {
  const { mediaId } = useParams();

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');

  const [mediaWikiTypeList, setMediaWikiTypeList] = useState([]);
  const [selecteMediaWikiType, setSelecteMediaWikiType] = useState('');

  const { appInfo } = useAppInfoStore();
  const { modal, closeModal } = useFormsModalStore();

  const [editorData, setEditorData] = useState('');

  const wikiMediaDetails = modal?.data;
  const isEditMode = wikiMediaDetails;

  // ----------------------------------------------------------------------

  const schema = Yup.object().shape({
    // MediaWikiTitle: Yup.string().required('وارد کردن عنوان الزامی است'),
    // MediaWikiSourceUrl: Yup.string().required('وارد کردن آدرس منبع الزامی است'),
  });

  const defaultValues = {
    MediaWikiTitle: wikiMediaDetails?.MediaWikiTitle || '',
    MediaWikiIsShort: wikiMediaDetails?.MediaWikiIsShort ? 1 : 0,
    MediaWikiSourceUrl: wikiMediaDetails?.MediaWikiSourceUrl || '',
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      MediaWikiMediaID: +mediaId,
      MediaWikiMediaWikiTypeID: selecteMediaWikiType,
      MediaWikiTitle: values?.MediaWikiTitle,
      MediaWikiBody: editorData,
      MediaWikiIsShort: +values?.MediaWikiIsShort,
      MediaWikiSourceUrl: values?.MediaWikiSourceUrl,
      MediaWikiLang: selectedLanguage,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateMediaWiki(wikiMediaDetails?.MediaWikiID, PAYLOAD);
        if (res?.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries([WIKI_MEDIA_LIST]);
          }
        } else {
          enqueueSnackbar('ثبت اطلاعات با مشکل مواجه شده است', { variant: 'error' });
        }
      } else {
        const res = await networkCreateMediaWiki(PAYLOAD);
        if (res?.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries([WIKI_MEDIA_LIST]);
        } else {
          enqueueSnackbar('ثبت اطلاعات با مشکل مواجه شده است', { variant: 'error' });
        }
      }
      setEditorData('');
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (appInfo) {
      const languages = generateSelectMenuItems(appInfo?.Language, 'MediaLanguageLable', 'MediaLanguageID');
      const mediaWikiTypes = generateSelectMenuItems(appInfo?.MediaWikiType, 'MediaWikiTypeLable', 'MediaWikiTypeID');

      setLanguages(languages);
      setSelectedLanguage(Number(wikiMediaDetails?.MediaWikiLang) || Number(languages?.[0]?.id)); // should fixed // todo

      setMediaWikiTypeList(mediaWikiTypes);
      setSelecteMediaWikiType(wikiMediaDetails?.MediaWikiTypeID || Number(mediaWikiTypes?.[0]?.id));

      if (isEditMode) {
        setEditorData(wikiMediaDetails?.MediaWikiBody);
      }
    }
  }, [appInfo, wikiMediaDetails]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ p: 5, overflow: 'visible' }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="MediaWikiTitle" type="text" label="عنوان" />

              <GeneralSelectMenu
                label="زبان محتوا"
                name="MediaWikiLang"
                options={languages}
                value={selectedLanguage}
                onChangeValue={setSelectedLanguage}
              />

              <GeneralSelectMenu
                label="نوع دانشنامه"
                name="MediaWikiMediaWikiTypeID"
                options={mediaWikiTypeList}
                value={selecteMediaWikiType}
                onChangeValue={setSelecteMediaWikiType}
              />

              <RHFTextField name="MediaWikiSourceUrl" type="text" label="آدرس منبع" />

              <RHFSwitch name="MediaWikiIsShort" labelPlacement="end" label="این متن کوتاه است؟" />

              <div />
            </Box>

            <Typography variant="body1" sx={{ mt: 5, mb: 2 }}>
              محتوای دانش نامه
            </Typography>

            <div className="mt-6">
              <GeneralEditor editorData={editorData} setEditorData={setEditorData} />
            </div>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                ذخیره و ادامه ویرایش
              </LoadingButton>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setEditorData('');
                  closeModal();
                }}
              >
                انصراف
              </Button>
            </div>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
